<script setup>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  authType: {
    type: String,
    required: true,
    validator: value => ['none', 'bearer', 'basic', 'api_key'].includes(value),
  },
  hasExistingSecret: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const SECRET_PLACEHOLDER = '••••••••';
const secretHelper = computed(() => {
  if (props.hasExistingSecret) {
    return t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_CONFIG.SECRET_SAVED');
  }
  return t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_CONFIG.SECRET_WRITE_ONLY');
});

const authConfig = defineModel('authConfig', {
  type: Object,
  default: () => ({}),
});

watch(
  () => props.authType,
  authType => {
    authConfig.value = authType === 'api_key' ? { location: 'header' } : {};
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-2">
    <div
      v-if="authType !== 'none'"
      class="flex items-start gap-2 rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-xs leading-relaxed text-n-slate-11"
      role="status"
      aria-live="polite"
    >
      <span
        class="i-lucide-shield-check mt-0.5 size-4 shrink-0 text-n-teal-11"
        aria-hidden="true"
      />
      {{ secretHelper }}
    </div>
    <Input
      v-if="authType === 'bearer'"
      v-model="authConfig.token"
      type="password"
      autocomplete="new-password"
      :label="t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_CONFIG.BEARER_TOKEN')"
      :placeholder="SECRET_PLACEHOLDER"
    />
    <template v-else-if="authType === 'basic'">
      <Input
        v-model="authConfig.username"
        :label="t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_CONFIG.USERNAME')"
        :placeholder="
          t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_CONFIG.USERNAME_PLACEHOLDER')
        "
      />
      <Input
        v-model="authConfig.password"
        type="password"
        autocomplete="new-password"
        :label="t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_CONFIG.PASSWORD')"
        :placeholder="SECRET_PLACEHOLDER"
      />
    </template>
    <template v-else-if="authType === 'api_key'">
      <fieldset class="flex flex-col gap-2">
        <legend class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_CONFIG.API_LOCATION') }}
        </legend>
        <div class="grid gap-2 sm:grid-cols-2">
          <label
            class="flex cursor-pointer items-center gap-2 rounded-lg border border-n-weak px-3 py-2 text-sm text-n-slate-12"
          >
            <input
              v-model="authConfig.location"
              type="radio"
              name="custom-tool-api-location"
              value="header"
            />
            {{ t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_CONFIG.API_LOCATION_HEADER') }}
          </label>
          <label
            class="flex cursor-pointer items-center gap-2 rounded-lg border border-n-weak px-3 py-2 text-sm text-n-slate-12"
          >
            <input
              v-model="authConfig.location"
              type="radio"
              name="custom-tool-api-location"
              value="query"
            />
            {{ t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_CONFIG.API_LOCATION_QUERY') }}
          </label>
        </div>
      </fieldset>
      <Input
        v-model="authConfig.name"
        :label="t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_CONFIG.API_KEY')"
        :placeholder="
          t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_CONFIG.API_KEY_PLACEHOLDER')
        "
      />
      <Input
        v-model="authConfig.key"
        type="password"
        autocomplete="new-password"
        :label="t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_CONFIG.API_VALUE')"
        :placeholder="SECRET_PLACEHOLDER"
      />
    </template>
  </div>
</template>
